import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db, googleProvider } from '../services/firebase';
import { signInWithEmailAndPassword, signInWithPopup, signOut, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { collection, query, where, getDocs, setDoc, doc, limit, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

// Admin-E-Mail-Liste
const ADMIN_EMAILS = [
  'haukecp@gmail.com',
  'admin@localandfresh.de',
  'hallo@localandfresh.de'
];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Memoize farm data fetching
  const fetchFarmData = useCallback(async (email) => {
    try {
      console.log('=== FARM DATA FETCH ===');
      console.log('Checking for email:', email);
      
      // Admin-Check: Wenn E-Mail in der Admin-Liste ist, keine Farm-Suche
      const isAdmin = ADMIN_EMAILS.includes(email.toLowerCase().trim());
      
      if (isAdmin) {
        console.log('Admin user detected:', email);
        return {
          email,
          farms: [],
          currentFarm: null,
          role: 'superadmin',
          isAdmin: true
        };
      }
      
      console.log('Regular user - searching for farms with email:', email);
      
      const farmsRef = collection(db, 'direkterzeuger');
      const searchEmail = email.toLowerCase().trim();
      
      // WICHTIG: Cache umgehen und direkt vom Server lesen
      const queryOptions = { source: 'server' };
      
      // Versuche verschiedene E-Mail-Felder und bereinige Whitespace
      const emailQueries = [
        query(farmsRef, where('mail', '==', searchEmail)),
        query(farmsRef, where('mail_public', '==', searchEmail))
      ];
      
      console.log('Trying email queries for:', searchEmail, '(bypassing cache)');
      
      let matchingFarms = [];
      for (const q of emailQueries) {
        try {
          const querySnapshot = await getDocs(q, queryOptions);
          console.log(`Query executed (server), found ${querySnapshot.size} farms`);
      
          if (querySnapshot.size > 0) {
            const farms = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
            matchingFarms.push(...farms);
          }
        } catch (error) {
          console.error('Error executing email query:', error);
        }
      }
      
      // Entferne Duplikate
      const uniqueFarms = matchingFarms.filter((farm, index, self) => 
        index === self.findIndex(f => f.id === farm.id)
      );
      
      console.log('Unique matching farms:', uniqueFarms.length);
      
      if (uniqueFarms.length > 0) {
        console.log('Farms found:', uniqueFarms);
        
        return {
          email,
          farms: uniqueFarms,
          currentFarm: uniqueFarms[0],
          role: 'admin'
        };
      }

      // Fallback: Manuelle Suche mit Whitespace-Bereinigung (auch vom Server)
      console.log('=== FALLBACK SEARCH (FROM SERVER) ===');
      try {
        const allFarmsSnapshot = await getDocs(farmsRef, queryOptions);
        console.log('All farms loaded from server:', allFarmsSnapshot.size);
        
        // Debug: Zeige alle "Bauer Ridder" Farms
        const bauerRidderFarms = [];
        allFarmsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.name && data.name.toLowerCase().includes('bauer ridder')) {
            bauerRidderFarms.push({
              id: doc.id,
              name: data.name,
              mail: data.mail,
              mail_public: data.mail_public
            });
          }
        });
        
        console.log('=== BAUER RIDDER FARMS (FROM SERVER) ===');
        console.log('Found Bauer Ridder farms:', bauerRidderFarms.length);
        bauerRidderFarms.forEach((farm, index) => {
          console.log(`Bauer Ridder ${index + 1}:`, JSON.stringify({
            id: farm.id,
            name: farm.name,
            mail: farm.mail,
            mail_public: farm.mail_public,
            mailLength: farm.mail ? farm.mail.length : 0,
            mailChars: farm.mail ? Array.from(farm.mail).map(c => c.charCodeAt(0)) : []
          }, null, 2));
        });
        
        // Debug: Umfassende Suche nach hallo@localandfresh.de in ALLEN Feldern
        const allFieldMatches = [];
        allFarmsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const docId = doc.id;
          
          // Durchsuche ALLE Felder nach der E-Mail-Adresse
          for (const [fieldName, fieldValue] of Object.entries(data)) {
            if (typeof fieldValue === 'string' && 
                fieldValue.toLowerCase().includes('hallo@localandfresh.de')) {
              allFieldMatches.push({
                id: docId,
                name: data.name,
                fieldName: fieldName,
                fieldValue: fieldValue,
                exactMatch: fieldValue === 'hallo@localandfresh.de',
                trimmedMatch: fieldValue.trim() === 'hallo@localandfresh.de'
              });
            }
          }
        });
        
        console.log('=== COMPREHENSIVE SEARCH FOR hallo@localandfresh.de ===');
        console.log('Found matches in all fields:', allFieldMatches.length);
        allFieldMatches.forEach((match, index) => {
          console.log(`All fields match ${index + 1}:`, JSON.stringify(match, null, 2));
        });
        
        // Debug: Speziell das Dokument NDiIIg5FrGvjXATYwvWD laden
        console.log('=== SPECIFIC DOCUMENT ANALYSIS ===');
        const specificDoc = allFarmsSnapshot.docs.find(doc => doc.id === 'NDiIIg5FrGvjXATYwvWD');
        if (specificDoc) {
          const specificData = specificDoc.data();
          console.log('Document NDiIIg5FrGvjXATYwvWD full data:', JSON.stringify({
            allFields: Object.keys(specificData),
            emailFields: Object.fromEntries(
              Object.entries(specificData).filter(([key, value]) => 
                key.toLowerCase().includes('mail') || 
                key.toLowerCase().includes('email') ||
                (typeof value === 'string' && value.includes('@'))
              )
            )
          }, null, 2));
        }
        
        // Debug: Suche nach hallo@localandfresh.de
        const emailMatches = [];
        allFarmsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (data.mail && data.mail.toLowerCase().includes('localandfresh')) {
            emailMatches.push({
              id: doc.id,
              name: data.name,
              mail: data.mail,
              mail_public: data.mail_public
            });
          }
        });
        
        console.log('=== LOCALANDFRESH EMAIL MATCHES (FROM SERVER) ===');
        console.log('Found localandfresh matches:', emailMatches.length);
        emailMatches.forEach((farm, index) => {
          console.log(`Match ${index + 1}:`, JSON.stringify({
            id: farm.id,
            name: farm.name,
            mail: farm.mail,
            mail_public: farm.mail_public,
            exactMatch: farm.mail === 'hallo@localandfresh.de',
            trimmedMatch: farm.mail.trim() === 'hallo@localandfresh.de'
          }, null, 2));
        });
        
        const manualMatch = allFarmsSnapshot.docs.find(doc => {
          const data = doc.data();
          if (!data.mail) return false;
          
          const cleanFarmMail = data.mail.toLowerCase().trim().replace(/\s+/g, '');
          const cleanSearchEmail = searchEmail.replace(/\s+/g, '');
          
          return cleanFarmMail === cleanSearchEmail;
        });
        
        if (manualMatch) {
          console.log('FALLBACK SEARCH FOUND MATCH:', manualMatch.id);
          const matchingFarms = [{
            id: manualMatch.id,
            ...manualMatch.data()
          }];
          
          return {
            email,
            farms: matchingFarms,
            currentFarm: matchingFarms[0],
            role: 'admin'
          };
        }
      } catch (debugError) {
        console.error('Fallback search failed:', debugError);
      }

      console.log('No farms found with email:', email);
      return null;
    } catch (error) {
      console.error('Error fetching farm data:', error);
      console.error('Error details:', error.code, error.message);
      throw error;
    }
  }, []);

  const switchFarm = useCallback(async (farmId) => {
    try {
      console.log('Switching to farm:', farmId);
      console.log('Available farms:', user?.farms);
      
      if (!user?.farms) {
        throw new Error('Keine Höfe verfügbar');
      }

      const newFarm = user.farms.find(farm => farm.id === farmId);
      if (!newFarm) {
        throw new Error('Hof nicht gefunden');
      }

      console.log('New farm found:', newFarm);

      // Aktualisiere den User-State mit dem neuen Farm
      const updatedUser = {
        ...user,
        currentFarm: newFarm,
        farmId: newFarm.id,
        farmName: newFarm.name,
        currentFarmId: newFarm.id
      };

      console.log('Updating user state with:', updatedUser);
      setUser(updatedUser);

      // Speichere die Auswahl im localStorage für Persistenz
      localStorage.setItem('selectedFarmId', newFarm.id);

      return { success: true };
    } catch (error) {
      console.error('Farm switch error:', error);
      return { success: false, error: error.message };
    }
  }, [user]);

  // Auth state change handler
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (authUser) => {
      try {
        if (authUser) {
          console.log('Auth state changed, user logged in:', authUser.email);
          
          // Bei nicht-verifizierten Nutzern direkt ausloggen
          if (!authUser.emailVerified) {
            console.log('User not verified, logging out');
            await signOut(auth);
            setUser(null);
            setError('Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Eine neue Bestätigungs-E-Mail wurde gesendet.');
            setLoading(false);
            setIsInitialized(true);
            return;
          }
          
          const userData = await fetchFarmData(authUser.email);
          
          if (userData) {
            console.log('Setting user with farm data:', userData);
            
            // Admin-User haben keine currentFarm, daher andere Logik
            if (userData.isAdmin) {
              setUser({ 
                ...authUser, 
                ...userData
              });
            } else {
              setUser({ 
                ...authUser, 
                ...userData,
                farmId: userData.currentFarm.id,
                farmName: userData.currentFarm.name,
                currentFarmId: userData.currentFarm.id
              });
            }
          } else {
            console.log('No farm found for email, logging out');
            await signOut(auth);
            setUser(null);
            setError('Kein Hof mit dieser E-Mail-Adresse gefunden');
          }
        } else {
          // Nur bei echtem Logout (auth.currentUser === null) den User-State zurücksetzen
          if (auth.currentUser === null) {
            console.log('Auth state changed, user logged out');
            setUser(null);
            setError(null);
          } else {
            console.log('Auth state changed, but not a real logout - keeping user state');
          }
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        await signOut(auth);
        setUser(null);
        setError(error.message);
      } finally {
        setLoading(false);
        setIsInitialized(true);
      }
    });

    return () => unsubscribe();
  }, [fetchFarmData]);

  const loginWithEmail = async (email, password) => {
    try {
      console.log('Starting loginWithEmail...');
      setError(null);
      setLoading(true);
      
      // Versuche Login
      console.log('Attempting login with email:', email);
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Login successful, user credential:', userCredential);
      
      // Prüfe E-Mail-Verifizierung direkt von Firebase Auth
      if (!userCredential.user.emailVerified) {
        console.log('User email not verified, sending verification email...');
        // Sende neue Bestätigungs-E-Mail
        await sendEmailVerification(userCredential.user);
        // Erzwinge Logout
        await signOut(auth);
        setUser(null);
        throw { code: 'auth/unverified-email' };
      }

      // Prüfe ob der Hof existiert
      console.log('Fetching farm data...');
      const farmData = await fetchFarmData(email);
      if (!farmData) {
        console.log('No farm found for email, logging out');
        await signOut(auth);
        setUser(null);
        throw new Error('Kein Hof mit dieser E-Mail-Adresse gefunden');
      }

      // Setze den User-State mit den Farm-Daten
      console.log('Setting user state with farm data:', farmData);
      
      // Admin-User haben keine currentFarm, daher andere Logik
      if (farmData.isAdmin) {
        const userWithFarmData = {
          ...userCredential.user,
          ...farmData
        };
        setUser(userWithFarmData);
      } else {
        const userWithFarmData = {
          ...userCredential.user,
          ...farmData,
          farmId: farmData.currentFarm.id,
          farmName: farmData.currentFarm.name,
          currentFarmId: farmData.currentFarm.id
        };
        setUser(userWithFarmData);
      }
      
      console.log('User state set:', farmData);

      // Aktualisiere nur den letzten Login-Zeitpunkt
      const userDoc = doc(db, 'users_hofportal', userCredential.user.uid);
      await setDoc(userDoc, {
        lastLogin: new Date().toISOString()
      }, { merge: true });

      console.log('Login process completed successfully');
      return { success: true };
    } catch (error) {
      console.error('Email login error:', error);
      
      // Bei Firebase-Fehlern direkt ausloggen
      if (error.code) {
        await signOut(auth);
        setUser(null);
      }
      
      setError(error.message);
      return { 
        success: false, 
        error: error.message,
        message: getErrorMessage(error.code)
      };
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const result = await signInWithPopup(auth, googleProvider);
      const farmData = await fetchFarmData(result.user.email);
      
      if (!farmData) {
        await signOut(auth);
        throw new Error('Kein Hof mit dieser E-Mail-Adresse gefunden');
      }
      
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      setError(error.message);
      return { 
        success: false, 
        error: error.message,
        message: getErrorMessage(error.code)
      };
    } finally {
      setLoading(false);
    }
  };

  const registerWithEmail = async (email, password, userData) => {
    try {
      console.log('Starting registerWithEmail...');
      setError(null);
      setLoading(true);
      
      // Prüfe ob der Hof existiert
      console.log('Checking if farm exists...');
      const farmData = await fetchFarmData(email);
      if (!farmData) {
        console.log('No farm found for email:', email);
        throw new Error('Kein Hof mit dieser E-Mail-Adresse gefunden');
      }
      console.log('Farm found:', farmData);

      // Erstelle den Benutzer in Firebase Auth
      console.log('Creating user in Firebase Auth...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('User created in Firebase Auth:', userCredential.user.uid);
      
      // Sende Bestätigungs-E-Mail
      console.log('Sending verification email...');
      await sendEmailVerification(userCredential.user);
      console.log('Verification email sent');
      
      // Speichere zusätzliche Benutzerdaten in Firestore
      console.log('Saving user data to Firestore...');
      const userDataToSave = {
        ...userData,
        email: email.toLowerCase(),
        farmId: farmData.currentFarm.id,
        farmName: farmData.currentFarm.name,
        createdAt: Date.now(),
        role: 'admin'
      };
      console.log('User data to save:', userDataToSave);
      console.log('User ID:', userCredential.user.uid);
      console.log('Auth token email:', userCredential.user.email);
      
      await setDoc(doc(db, 'users_hofportal', userCredential.user.uid), userDataToSave);
      console.log('User data saved to Firestore');

      return { 
        success: true,
        message: 'Registrierung erfolgreich. Bitte bestätigen Sie Ihre E-Mail-Adresse.'
      };
    } catch (error) {
      console.error('Register error:', error);
      let errorMessage = 'Registrierung fehlgeschlagen';
      
      // Firebase-spezifische Fehlermeldungen
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Diese E-Mail-Adresse wird bereits verwendet';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Ungültige E-Mail-Adresse';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'E-Mail/Passwort-Anmeldung ist nicht aktiviert';
          break;
        case 'auth/weak-password':
          errorMessage = 'Das Passwort ist zu schwach';
          break;
        default:
          errorMessage = error.message;
      }
      
      console.log('Setting error message:', errorMessage);
      setError(errorMessage);
      return { 
        success: false, 
        error: errorMessage,
        message: errorMessage
      };
    } finally {
      console.log('Setting loading to false');
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      setLoading(true);
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'Kein Benutzer mit dieser E-Mail-Adresse gefunden';
      case 'auth/wrong-password':
        return 'Falsches Passwort';
      case 'auth/invalid-email':
        return 'Ungültige E-Mail-Adresse';
      case 'auth/user-disabled':
        return 'Dieser Account wurde deaktiviert';
      case 'auth/too-many-requests':
        return 'Zu viele fehlgeschlagene Anmeldeversuche. Bitte versuchen Sie es später erneut';
      case 'auth/network-request-failed':
        return 'Netzwerkfehler. Bitte überprüfen Sie Ihre Internetverbindung';
      case 'auth/email-already-in-use':
        return 'Diese E-Mail-Adresse wird bereits verwendet';
      case 'auth/weak-password':
        return 'Das Passwort ist zu schwach';
      case 'auth/operation-not-allowed':
        return 'E-Mail/Passwort-Anmeldung ist nicht aktiviert';
      case 'auth/popup-closed-by-user':
        return 'Google-Anmeldung wurde abgebrochen';
      case 'auth/popup-blocked':
        return 'Pop-up wurde blockiert. Bitte erlauben Sie Pop-ups für diese Seite';
      case 'auth/cancelled-popup-request':
        return 'Anmeldung wurde abgebrochen';
      case 'auth/account-exists-with-different-credential':
        return 'Ein Account mit dieser E-Mail existiert bereits mit einer anderen Anmeldemethode';
      case 'auth/unverified-email':
        return 'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Eine neue Bestätigungs-E-Mail wurde gesendet.';
      case 'auth/invalid-login-credentials':
        return 'Diese E-Mail Adresse ist uns nicht bekannt.';
      default:
        return 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut';
    }
  };

  const value = {
    user,
    loading,
    error,
    isInitialized,
    loginWithEmail,
    loginWithGoogle,
    registerWithEmail,
    logout,
    switchFarm,
    currentFarmId: user?.currentFarmId || user?.farmId || null
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};